import { useState } from "react";
import { Accordion, Badge, Button, Card, Col, Modal, Row } from "react-bootstrap";
import HelpFinderPanel from "../components/HelpFinderPanel";

const keyPrograms = [
  {
    title: "Veterans Resource Navigators",
    description: "Personalized one-on-one guidance connecting veterans with housing, healthcare, employment, benefits, and essential support services.",
  },
  {
    title: "Partner Network Management",
    description: "Building and maintaining relationships with trusted agencies, food banks, and service providers to improve access to critical resources.",
  },
  {
    title: "Direct Bridge Assistance",
    description: "Rapid support, including food assistance and short-term relief, to help veterans during times of crisis.",
  },
  {
    title: "Community Outreach",
    description: "Direct engagement with veterans in the community to raise awareness and identify unmet needs.",
  },
];

const coreServices = [
  {
    title: "Food Assistance",
    details: "We offer emergency food relief and long-term support through partnerships with local food banks, businesses, and community organizations, ensuring veterans and families have access to nutritious meals.",
  },
  {
    title: "Housing Support",
    details: "We work with veterans facing homelessness or housing instability by providing referrals to emergency shelters, assisting with rent payments, and connecting them to longer-term housing options.",
  },
  {
    title: "Transportation Solutions",
    details: "We help veterans access critical transportation options—providing bus passes, arranging rideshare services, and connecting them with programs for medical appointments and job interviews.",
  },
  {
    title: "Resource Navigation & Benefits",
    details: "We guide veterans through complex benefit systems and connect them with employment, healthcare, mental health services, and financial counseling programs.",
  },
];

const strategyPillars = [
  {
    num: "1",
    title: "Assess & Personalize",
    body: "We understand individual needs through personalized outreach and tailor support to each veteran's unique circumstances.",
  },
  {
    num: "2",
    title: "Guide Step-by-Step",
    body: "We provide clear, actionable pathways through complex systems and help veterans understand each step of the process.",
  },
  {
    num: "3",
    title: "Build Strong Partnerships",
    body: "We maintain active relationships with local agencies and service providers to improve referrals and expand available resources.",
  },
  {
    num: "4",
    title: "Follow Up & Support",
    body: "We don't just make referrals; we follow up to help veterans stay connected to support options.",
  },
];

function ProgramsPage() {
  const [showHelpModal, setShowHelpModal] = useState(false);

  return (
    <section>
      {/* Hero Section */}
      <Card className="glass-card text-light mb-4 border-warning">
        <Card.Body>
          <h1 className="section-title mb-3">Our Programs & Services</h1>
          <p className="text-muted-soft lead mb-0">We provide hands-on support that meets veterans where they are and helps them move toward greater stability, nourishment, and renewed purpose.</p>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mb-4 border-info">
        <Card.Body>
          <h2 className="h4 mb-2 fw-bold">Find Help</h2>
          <p className="text-muted-soft mb-3">
            Use our resource help tool to search trusted programs, services, and support options for veterans and families. Filter by category, location, and service type to find the right fit.
          </p>
          <Button className="cta-btn" onClick={() => setShowHelpModal(true)}>
            Open Help Finder
          </Button>
        </Card.Body>
      </Card>

      {/* Key Programs */}
      <div className="mb-4">
        <h2 className="h4 mb-3 text-light">Key Programs</h2>
        <Row className="g-3">
          {keyPrograms.map((program, idx) => (
            <Col
              lg={6}
              key={idx}
            >
              <Card className="glass-card h-100 text-light">
                <Card.Body>
                  <h3 className="h6 mb-2 text-warning">{program.title}</h3>
                  <p className="text-muted-soft mb-0">{program.description}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Core Services */}
      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h2 className="h4 mb-3">Core Services</h2>
          <Accordion
            flush
            alwaysOpen
          >
            {coreServices.map((service, idx) => (
              <Accordion.Item
                eventKey={idx.toString()}
                key={service.title}
                className="home-accordion-item"
              >
                <Accordion.Header className="fw-semibold text-light">{service.title}</Accordion.Header>
                <Accordion.Body className="text-muted-soft">{service.details}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      {/* Our Strategy */}
      <div className="mb-4">
        <h2 className="h4 mb-3 text-light">Our Strategy in Action</h2>
        <p className="text-muted-soft mb-3">Our strategy is rooted in action, compassion, and accountability. As a small local group, we focus on practical support and measurable progress for the veterans we serve.</p>
        <Row className="g-3">
          {strategyPillars.map((pillar) => (
            <Col
              md={6}
              key={pillar.num}
            >
              <Card className="glass-card text-light h-100">
                <Card.Body>
                  <div className="d-flex align-items-start gap-3">
                    <Badge
                      bg="primary"
                      text="light"
                      className="fs-5 flex-shrink-0 strategy-pillar-badge"
                      style={{ minWidth: "2.5rem", height: "2.5rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      {pillar.num}
                    </Badge>
                    <div>
                      <h3 className="h6 mb-2 text-warning">{pillar.title}</h3>
                      <p className="text-muted-soft mb-0 small">{pillar.body}</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Mission of Hope */}
      <Card className="glass-card text-light mb-4 border-info">
        <Card.Body>
          <h2 className="h4 mb-3">A Mission of Hope</h2>
          <p className="text-muted-soft mb-2">Hope is not just a message. Through our programs and partnerships, we work to:</p>
          <ul className="text-muted-soft mb-0">
            <li className="mb-2">Promote dignity, purpose, and community connection</li>
            <li className="mb-2">Share success stories that inspire others to seek help</li>
            <li>Empower veterans to move from crisis to stability, and from stability to strength</li>
          </ul>
        </Card.Body>
      </Card>

      {/* Program Delivery Priorities */}
      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h2 className="h4 mb-3">Program Delivery Priorities</h2>
          <Row className="g-3">
            <Col md={4}>
              <div className="text-center">
                <div className="text-warning fw-bold fs-5 mb-2">Fast Access</div>
                <p className="text-muted-soft small mb-0">Quick connection to support options with minimal barriers</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <div className="text-warning fw-bold fs-5 mb-2">Reduced Barriers</div>
                <p className="text-muted-soft small mb-0">Streamlined processes that respect veteran time and dignity</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <div className="text-warning fw-bold fs-5 mb-2">Continuity</div>
                <p className="text-muted-soft small mb-0">Ongoing support and follow-up after first contact</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Success Metrics */}
      <Card className="glass-card text-light">
        <Card.Body>
          <h2 className="h4 mb-3">How We Measure Success</h2>
          <div className="mb-3">
            <h3 className="h6 text-warning mb-2">Veteran Assistance Outcomes</h3>
            <p className="text-muted-soft mb-0">We track referrals, follow-ups, and confirmed service connections to measure effectiveness and improve how we support veterans over time.</p>
          </div>
          <div className="mb-3">
            <h3 className="h6 text-warning mb-2">Quarterly Partner Engagement</h3>
            <p className="text-muted-soft mb-0">We conduct regular check-ins with local service providers and community partners to keep partnerships active, collaborative, and aligned with our mission and core values.</p>
          </div>
          <div>
            <h3 className="h6 text-warning mb-2">Continuous Improvement</h3>
            <p className="text-muted-soft mb-0">Through data tracking, follow-up communication, and partnership evaluations, we monitor performance and refine our processes so services stay practical and mission-driven.</p>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} dialogClassName="help-finder-modal">
        <Modal.Header closeButton>
          <Modal.Title>Find Help</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2 p-md-3">
          <HelpFinderPanel />
        </Modal.Body>
      </Modal>
    </section>
  );
}

export default ProgramsPage;
